import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { doc, getDoc, updateDoc, serverTimestamp, arrayUnion, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DisplayStand, Publication } from '../types';
import MaintenanceRequestModal from './MaintenanceRequestModal';
import StandHeader from './public/StandHeader';
import CurrentPoster from './public/CurrentPoster';
import PublicationsList from './public/PublicationsList';
import ReservationInfo from './public/ReservationInfo';
import MaintenanceButton from './public/MaintenanceButton';
import LoadingSpinner from './public/LoadingSpinner';
import NotFoundView from './public/NotFoundView';
import PublicStockModal from './PublicStockModal';
import { addDays, format } from 'date-fns';
import { addHistoryRecord } from '../utils/historyUtils';
import { generateId } from '../utils/id';
import { createNotification, ORG_BROADCAST_USER_ID } from '../lib/notifications';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../context/AuthContext';

const PublicStandView: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const [stand, setStand] = useState<DisplayStand | null>(null);
    const reserveInputRef = useRef<HTMLInputElement | null>(null);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [posterImageUrl, setPosterImageUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [showMaintenanceRequestModal, setShowMaintenanceRequestModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [reserveName, setReserveName] = useState('');
  const [reserveLoading, setReserveLoading] = useState(false);
  const [reserveDays, setReserveDays] = useState(7);
  const { currentOrganization } = useOrganization();
  const { currentUser } = useAuth();

  useEffect(() => {
    const loadStand = async () => {
      if (!id) return;

      try {
        const standDoc = await getDoc(doc(db, 'stands', id));
        if (!standDoc.exists()) {
          toast.error('Présentoir non trouvé');
          return;
        }

        const standData = { id: standDoc.id, ...standDoc.data() } as DisplayStand;
        setStand(standData);

        // Load current poster image if available by name
        if (standData.currentPoster) {
          try {
            const postersQ = query(collection(db, 'posters'), where('name', '==', standData.currentPoster));
            const postersSnap = await getDocs(postersQ);
            const first = postersSnap.docs[0];
            if (first?.exists()) {
              const pData: any = first.data();
              setPosterImageUrl(pData.imageUrl);
            } else {
              setPosterImageUrl(undefined);
            }
          } catch (e) {
            console.error('Error loading poster image:', e);
            setPosterImageUrl(undefined);
          }
        } else {
          setPosterImageUrl(undefined);
        }

        if (standData.publications?.length > 0) {
          const publicationPromises = standData.publications.map(pub => 
            getDoc(doc(db, 'publications', pub.publicationId))
          );
          const publicationDocs = await Promise.all(publicationPromises);
          const publicationsData = publicationDocs
            .filter(doc => doc.exists())
            .map(doc => ({ id: doc.id, ...doc.data() })) as Publication[];
          setPublications(publicationsData);
        }
      } catch (error) {
        console.error('Error loading stand:', error);
        toast.error('Erreur lors du chargement du présentoir');
      } finally {
        setLoading(false);
      }
    };

    loadStand();
  }, [id]);

  // Auto-open flows via query params: ?action=reserve|stock[&days=7]
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    const days = params.get('days');

    if (days) {
      const n = parseInt(days, 10);
      if (!Number.isNaN(n) && n > 0) setReserveDays(n);
    }

    if (action === 'stock') {
      setShowStockModal(true);
    }
    if (action === 'reserve') {
      // Focus the name input to guide the user
      setTimeout(() => reserveInputRef.current?.focus(), 0);
    }
  }, [location.search]);

  const handleMaintenanceRequest = async (data: { description: string; issues: string }) => {
    if (!stand) return;

    try {
      const standRef = doc(db, 'stands', stand.id);
      await updateDoc(standRef, {
        maintenanceHistory: [
          ...(stand.maintenanceHistory || []),
          {
            id: crypto.randomUUID(),
            type: 'curative',
            date: new Date().toISOString(),
            issues: data.issues,
            description: data.description,
            status: 'pending',
            requestedBy: stand.reservedBy || 'Anonyme'
          }
        ],
        lastUpdated: serverTimestamp()
      });

      toast.success('Demande de maintenance envoyée avec succès');
      setShowMaintenanceRequestModal(false);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      toast.error('Erreur lors de l\'envoi de la demande');
    }
  };

  const handleQuickReserve = async () => {
    if (!stand) return;
    const name = reserveName.trim();
    if (name.length < 2) {
      toast.error('Le nom doit contenir au moins 2 caractères');
      return;
    }

    try {
      setReserveLoading(true);
      const standRef = doc(db, 'stands', stand.id);
      const startDate = new Date();
      const endDate = addDays(startDate, reserveDays);
      const reservedUntil = endDate.toISOString();

      const reservationData = {
        reservedBy: name,
        startDate: startDate.toISOString(),
        endDate: reservedUntil,
        isPerpetual: false,
        createdAt: new Date().toISOString()
      };

      const historyRecord = {
        id: generateId(),
        type: 'reservation',
        performedBy: (currentUser?.displayName || currentUser?.email || name),
        date: new Date().toISOString(),
        details: {
          action: 'create',
          previousState: {
            isReserved: !!stand.isReserved,
            reservedBy: stand.reservedBy || null,
            reservedUntil: stand.reservedUntil || null,
            isPerpetual: !!stand.isPerpetual
          },
          newState: {
            isReserved: true,
            reservedBy: name,
            reservedUntil,
            isPerpetual: false
          }
        }
      } as any;

      await updateDoc(standRef, {
        isReserved: true,
        reservedBy: name,
        reservedUntil,
        isPerpetual: false,
        lastUpdated: serverTimestamp(),
        reservationHistory: arrayUnion(reservationData),
        history: arrayUnion(historyRecord)
      });

      if (currentOrganization?.id) {
        await createNotification(
          currentOrganization.id,
          ORG_BROADCAST_USER_ID,
          {
            type: 'info',
            title: 'Nouvelle réservation',
            message: `${stand.name} réservé par ${name} (jusqu'au ${format(endDate, 'dd/MM/yyyy')})`,
            link: `/stands/${stand.id}`,
            category: 'reservation'
          }
        );
      }

      toast.success('Présentoir réservé');
      setStand({
        ...stand,
        isReserved: true,
        reservedBy: name,
        reservedUntil,
        isPerpetual: false,
        history: [...(stand.history || []), historyRecord],
        reservationHistory: [...(stand.reservationHistory || []), reservationData]
      });
      setReserveName('');
    } catch (error) {
      console.error('Erreur de réservation:', error);
      toast.error('Erreur lors de la réservation');
    } finally {
      setReserveLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!stand || !stand.isReserved) return;
    try {
      const standRef = doc(db, 'stands', stand.id);
      const newHistory = addHistoryRecord(stand, 'reservation', (currentUser?.displayName || currentUser?.email || 'Utilisateur'), {
        action: 'cancel',
        previousState: {
          reservedBy: stand.reservedBy,
          reservedUntil: stand.reservedUntil
        }
      });

      await updateDoc(standRef, {
        isReserved: false,
        reservedBy: null,
        reservedUntil: null,
        isPerpetual: false,
        lastUpdated: serverTimestamp(),
        history: newHistory
      });

      toast.success('Réservation annulée');
      setStand({ ...stand, isReserved: false, reservedBy: null, reservedUntil: null, isPerpetual: false, history: newHistory });
    } catch (error) {
      console.error('Erreur annulation:', error);
      toast.error('Erreur lors de l\'annulation');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!stand) return <NotFoundView />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="card p-8 bg-white shadow-xl border-2 border-blue-100">
          <div className="space-y-6">
            <StandHeader stand={stand} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CurrentPoster posterName={stand.currentPoster} posterImageUrl={posterImageUrl} />

              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Publications disponibles
                </h2>
                <PublicationsList publications={publications.map((p) => {
                  const found = stand.publications?.find(sp => sp.publicationId === p.id);
                  return { ...p, currentQuantity: found?.quantity } as any;
                })} />
              </div>
            </div>

            <ReservationInfo stand={stand} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="card p-4">
                <h3 className="text-md font-semibold mb-3">Prendre ce présentoir</h3>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Votre nom"
                    className="input w-full"
                    value={reserveName}
                    onChange={(e) => setReserveName(e.target.value)}
                    ref={reserveInputRef}
                  />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <label className="text-sm text-gray-700">Durée</label>
                  <select className="input w-40" value={reserveDays} onChange={(e) => setReserveDays(parseInt(e.target.value) || 7)}>
                    <option value={1}>1 jour</option>
                    <option value={3}>3 jours</option>
                    <option value={7}>7 jours</option>
                    <option value={14}>14 jours</option>
                    <option value={30}>30 jours</option>
                  </select>
                </div>
                <button
                  className="btn btn-primary w-full"
                  onClick={handleQuickReserve}
                  disabled={reserveLoading}
                >
                  {reserveLoading ? 'Réservation...' : `Réserver pour ${reserveDays} jour(s)`}
                </button>
                {stand.isReserved && (
                  <button
                    className="btn w-full mt-2 bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                    onClick={handleCancelReservation}
                  >
                    Rendre le présentoir
                  </button>
                )}
              </div>

              <div className="card p-4">
                <h3 className="text-md font-semibold mb-3">J'ai utilisé des publications</h3>
                <p className="text-sm text-gray-600 dark:text-white mb-3">Saisissez la quantité restante après utilisation.</p>
                <button
                  className="btn btn-primary w-full"
                  onClick={() => setShowStockModal(true)}
                >
                  Mettre à jour le stock
                </button>
              </div>
            </div>

            <MaintenanceButton onClick={() => setShowMaintenanceRequestModal(true)} />
          </div>
        </div>
      </div>

      {showMaintenanceRequestModal && (
        <MaintenanceRequestModal
          stand={stand}
          isOpen={showMaintenanceRequestModal}
          onClose={() => setShowMaintenanceRequestModal(false)}
          onSubmit={handleMaintenanceRequest}
        />
      )}

      {showStockModal && stand && (
        <PublicStockModal
          stand={stand}
          isOpen={showStockModal}
          onClose={() => setShowStockModal(false)}
          publications={publications}
        />
      )}
    </div>
  );
};

export default PublicStandView;